import { resolveFieldType } from "./normalizeFieldType";
import { parseBoolean } from "./parseBoolean";
import type {
  QuizAnswerDetail,
  QuizConfig,
  QuizResult,
  QuizScoring,
} from "../../types/quiz";

export const DEFAULT_QUIZ_CONFIG: QuizConfig = {
  scoring: "end",
  showCorrectAnswers: true,
  passScore: 70,
};

export function parseQuizScoring(raw: unknown): QuizScoring {
  const value = String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
  return value === "per_question" ? "per_question" : "end";
}

export function parseQuizPassScore(raw: unknown): number | null {
  if (raw == null || raw === "") return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return null;
  return Math.min(100, Math.max(0, parsed));
}

export function parseQuizCorrect(raw: unknown): string[] {
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    return trimmed ? [trimmed] : [];
  }
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  for (const item of raw) {
    const value = String(item ?? "").trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    result.push(value);
  }
  return result;
}

export function parseQuizPoints(raw: unknown): number {
  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed <= 0) return 1;
  return parsed;
}

export function parseQuizConfig(raw: unknown): QuizConfig {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) {
    return { ...DEFAULT_QUIZ_CONFIG };
  }
  const record = raw as Record<string, unknown>;
  const showRaw = record.showCorrectAnswers ?? record.show_correct_answers;
  return {
    scoring: parseQuizScoring(record.scoring),
    showCorrectAnswers:
      showRaw == null ? DEFAULT_QUIZ_CONFIG.showCorrectAnswers : parseBoolean(showRaw),
    passScore: parseQuizPassScore(record.passScore ?? record.pass_score),
  };
}

export function readQuizConfig(
  source: { quiz?: unknown } | null | undefined,
): QuizConfig | null {
  if (!source || source.quiz == null) return null;
  return parseQuizConfig(source.quiz);
}

export function resolveQuizConfig(
  source: { quiz?: unknown } | null | undefined,
): QuizConfig {
  return readQuizConfig(source) ?? { ...DEFAULT_QUIZ_CONFIG };
}

export function serializeQuizConfig(config: QuizConfig): QuizConfig {
  return {
    scoring: config.scoring === "per_question" ? "per_question" : "end",
    showCorrectAnswers: Boolean(config.showCorrectAnswers),
    passScore: parseQuizPassScore(config.passScore),
  };
}

export function serializeQuizCorrect(
  type: string | null | undefined,
  values: string[],
): string | string[] | undefined {
  const unique = parseQuizCorrect(values);
  if (unique.length === 0) return undefined;
  if (unique.length === 1 && resolveFieldType(type) !== "multiple-choice") {
    return unique[0];
  }
  return unique;
}

function readNumber(raw: unknown, fallback = 0): number {
  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function parseQuizDetail(raw: unknown): QuizAnswerDetail | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const fieldId = Number(record.fieldId ?? record.field_id);
  const name = String(record.name ?? "").trim();
  if (!Number.isFinite(fieldId) || !name) return null;
  const correctAnswersRaw = record.correctAnswers ?? record.correct_answers;
  const detail: QuizAnswerDetail = {
    fieldId,
    name,
    correct: parseBoolean(record.correct),
    points: readNumber(record.points),
    maxPoints: readNumber(record.maxPoints ?? record.max_points),
  };
  if (correctAnswersRaw != null) {
    detail.correctAnswers = parseQuizCorrect(correctAnswersRaw);
  }
  return detail;
}

export function parseQuizResult(raw: unknown): QuizResult | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const detailsRaw = record.details;
  const passedRaw = record.passed;
  return {
    score: readNumber(record.score),
    maxScore: readNumber(record.maxScore ?? record.max_score),
    percent: readNumber(record.percent),
    passed: passedRaw == null ? null : parseBoolean(passedRaw),
    scoring: parseQuizScoring(record.scoring),
    details: Array.isArray(detailsRaw)
      ? detailsRaw.flatMap((item) => {
          const detail = parseQuizDetail(item);
          return detail ? [detail] : [];
        })
      : [],
  };
}

export function parseQuizCheckResult(raw: unknown): QuizAnswerDetail | null {
  return parseQuizDetail(raw);
}

export function quizCheckValue(raw: string): unknown {
  const trimmed = raw.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("[") || trimmed.startsWith("{")) {
    try {
      return JSON.parse(trimmed) as unknown;
    } catch {
      return trimmed;
    }
  }
  return trimmed;
}
